import React, { useState, useCallback } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import BetaLandingPage from './BetaLandingPage';

/**
 * BetaLoginFlow — state machine parent for the private beta login flow.
 *
 * New users onboard via the magic-link email invite (handled outside this
 * component by InviteAcceptPage). This component only handles:
 *
 *   'landing' → BetaLandingPage (informational)
 *   'signin'  → Authenticator (existing, for returning users)
 */
const BetaLoginFlow = () => {
  const [currentStep, setCurrentStep] = useState('landing');

  /**
   * Navigate to the Authenticator for returning users.
   */
  const handleGoToSignIn = useCallback(() => {
    setCurrentStep('signin');
  }, []);

  /**
   * Return to the landing page from the sign-in step.
   */
  const handleBackToLanding = useCallback(() => {
    setCurrentStep('landing');
  }, []);

  /* ── Render the current step ───────────────────────────────── */

  switch (currentStep) {
    case 'signin':
      return (
        <SignInWrapper onBack={handleBackToLanding} />
      );

    case 'landing':
    default:
      return (
        <BetaLandingPage
          onSignIn={handleGoToSignIn}
        />
      );
  }
};

/**
 * SignInWrapper — renders the existing Amplify Authenticator for
 * returning users, with a back button to return to the landing page.
 *
 * When the user signs in successfully, the Authenticator renders its
 * children. We detect this and reload the page so App.js handles the
 * full authenticated app rendering (avoiding circular dependency with
 * AuthenticatedApp).
 */
const SignInWrapper = ({ onBack }) => {
  return (
    <div style={styles.signinWrapper}>
      <div style={styles.backButtonContainer}>
        <button
          type="button"
          onClick={onBack}
          data-testid="signin-back-button"
          style={styles.backButton}
        >
          ← Back to landing page
        </button>
      </div>
      <Authenticator
        hideSignUp={true}
      >
        {/* When Authenticator renders children, the user is signed in.
            Reload so App.js picks up the session and renders the full app. */}
        <SignInComplete />
      </Authenticator>
    </div>
  );
};

/**
 * SignInComplete — rendered inside <Authenticator> once the user is
 * authenticated. Triggers a page reload so App.js handles the full
 * app rendering with AuthenticatedApp.
 */
const SignInComplete = () => {
  // The user just signed in via Authenticator. Reload so App.js
  // detects the active session and renders AuthenticatedApp.
  React.useEffect(() => {
    window.location.reload();
  }, []);

  return (
    <div style={styles.loadingWrapper}>
      Loading TravelFlow...
    </div>
  );
};

/* ── Styles ──────────────────────────────────────────────────── */

const styles = {
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '500',
  },

  signinWrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    boxSizing: 'border-box',
  },

  backButtonContainer: {
    width: '100%',
    maxWidth: '440px',
    marginBottom: '16px',
  },

  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: '8px 16px',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
};

export default BetaLoginFlow;
